<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue'
import isEmail from 'validator/es/lib/isEmail'
import isURL from 'validator/es/lib/isURL'
import type { Props } from './InputShellText.vue'

const t2 = useT2Base({
  Email: 'El. paštas',
  Text: 'Tekstas',
  'Bad URL': 'Netinkamas URL',
  'Has to be a valid email': 'Turi būti galiojantis el. paštas',
})

type TextType = 'text' | 'url' | 'email' | 'email:login' | 'email:register'

const props = defineProps<{
  label?: stringI18n
  optional?: boolean
  disabled?: boolean
  placeholder?: string
  before?: string
  after?: string
  type?: TextType
  validate?: MyInputError<string>[]
  beforeLabel?: 'never'
  afterLabel?: 'never'
  width?: Props['width']
}>()

const holder = defineModel<string | None>({ default: '' })
const focus = ref(false)
const elem = ref<HTMLInputElement>()
const emit = defineEmits<{ focus: [e: boolean] }>()

function focused(value: boolean) {
  emit('focus', value)
  focus.value = value
}

type UiValue = {
  label?: stringI18n
  ph?: string
  after?: string
  before?: string
  attrs?: InputHTMLAttributes
}

const map: Record<TextType, UiValue> = {
  'email:login': {
    ph: 'example@example.com',
    label: t2('Email'),
    attrs: { autocomplete: 'username', name: 'username', type: 'email' },
  },
  email: {
    label: t2('Email'),
    ph: 'example@example.com',
    attrs: { autocomplete: 'off', name: 'username', type: 'email' },
  },
  'email:register': {
    label: t2('Email'),
    ph: 'example@example.com',
    attrs: { autocomplete: 'username', name: 'username', type: 'email' },
  },
  url: {
    label: ts('URL'),
    ph: 'https://linenbylinas.com',
    attrs: { type: 'url' },
  },
  text: { label: t2('Text'), ph: undefined, attrs: { type: 'text' } },
}

const attrs = useAttrs() as InputHTMLAttributes

const ui = computed(() => {
  const { type, label: label2, placeholder, after, before } = props
  const ui: UiValue = (type && map[type]) || map.text
  const label = label2 ?? ui.label ?? t2('Text')
  return {
    label,
    ph: placeholder ?? ui.ph ?? label,
    after: after ?? ui.after,
    before: before || ui.before,
    attrs: { ...attrs, ...ui.attrs },
  }
})

const validateUrl: MyInputError<string> = { msg: t2('Bad URL'), isErr: (i) => !isURL(i) }
const validateEmail: MyInputError<string> = { msg: t2('Has to be a valid email'), isErr: (i) => !isEmail(i) }

const myInput = useMyInput<string>({
  label: () => ui.value.label,
  optional: () => props.optional,
  value: () => holder.value ?? '',
  validate() {
    const { type } = props
    const email = type === 'email:login' || type === 'email' || type === 'email:register'
    return [type === 'url' && validateUrl, email && validateEmail, ...helpers.arr(props.validate)]
  },
})

const error = useMyInputValidate(myInput, {
  focus: () => focus.value,
  disabled: () => props.disabled,
})

defineExpose({
  ...myInput.expose(),
  focus(): void {
    elem.value?.focus()
  },
})
const active = computed(() => !!(holder.value || error.value))
</script>

<template>
  <InputShellText :label="ui.label" :active :optional :disabled :error :focus :before="ui.before" :width :after="ui.after" :labelid="myInput.id">
    <template #default="{ classInput }">
      <input
        :id="myInput.id"
        ref="elem"
        v-bind="ui.attrs"
        :value="holder"
        dir="ltr"
        tabindex="0"
        spellcheck="false"
        autocapitalize="off"
        :disabled="disabled"
        :placeholder="ui.ph"
        :class="classInput"
        @input.passive="(i) => (holder = myInputValue(i, (i) => i?.toString() || ''))"
        @focus.passive="() => focused(true)"
        @blur.passive="() => focused(false)"
      />
    </template>

    <template v-for="(_, name) in $slots" #[name]="slotData"><slot :name="name" v-bind="slotData" /></template>
  </InputShellText>
</template>
